import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';

interface Rule {
  conditions: Condition[];
  then: {
    type: 'fixed' | 'logic';
    value: number;
    column?: string;
    operator?: string;
  };
  description: string;
  caseSensitive: boolean;
}

interface Condition {
  condition: string;
  operator: string;
  value: any;
  logicOperator?: 'AND' | 'OR';
}

@Component({
  selector: 'app-dynamic-logic-builder',
  templateUrl: './dynamic-logic-builder.component.html',
  styleUrls: ['./dynamic-logic-builder.component.css']
})
export class DynamicLogicBuilderComponent {
  @Input() projectColumns: string[] = [];
  @Input() users: any[] = [];
  @Input() groups: any[] = [];
  @Output() logicBuilt = new EventEmitter<any>();

  logicForm: FormGroup;

  operators = [
    { value: '==', label: 'Is equal to' },
    { value: '!=', label: 'Is not equal to' },
    { value: '>', label: 'Is greater than' },
    { value: '<', label: 'Is less than' },
    { value: '>=', label: 'Is greater than or equal to' },
    { value: '<=', label: 'Is less than or equal to' },
    { value: 'in', label: 'Is in list' },
    { value: 'notNullOrEmpty', label: 'Is not null or empty' },
    { value: 'isPresent', label: 'Is present' },
    { value: 'isNotPresent', label: 'Is not present' }
  ];

  logicOperators = [
    { value: 'AND', label: 'AND' },
    { value: 'OR', label: 'OR' }
  ];

  arithmeticOperators = [
    { value: '+', label: 'Add' },
    { value: '-', label: 'Subtract' },
    { value: '*', label: 'Multiply' },
    { value: '/', label: 'Divide' }
  ];

  constructor(private fb: FormBuilder) {
    this.logicForm = this.fb.group({
      conditions: this.fb.array([this.createCondition(true)]),
      thenType: ['logic', Validators.required],
      thenColumn: ['', Validators.required],
      thenOperator: [''],
      thenValue: ['', Validators.required],
      caseSensitive: [false]
    });

    // Subscribe to thenType changes to handle validation
    this.logicForm.get('thenType')?.valueChanges.subscribe(value => {
      const thenOperator = this.logicForm.get('thenOperator');
      const thenColumn = this.logicForm.get('thenColumn');
      
      if (value === 'logic') {
        thenOperator?.setValidators(Validators.required);
        thenColumn?.setValidators(Validators.required);
      } else {
        thenOperator?.clearValidators();
        thenColumn?.clearValidators();
      }
      
      thenOperator?.updateValueAndValidity();
      thenColumn?.updateValueAndValidity();
    });
  }

  get conditions() {
    return this.logicForm.get('conditions') as FormArray;
  }

  get equation() {
    return this.logicForm.get('result.equation') as FormArray;
  }

  createCondition(isFirst: boolean = false): FormGroup {
    // Define an interface for the condition structure
    interface ConditionConfig {
      condition: (string | ((control: AbstractControl) => ValidationErrors | null))[];
      operator: (string | ((control: AbstractControl) => ValidationErrors | null))[];
      value: string[];
      valueMode?: string[];  // 'users' | 'groups' only for certain conditions
      logicOperator?: string[];  // Make logicOperator optional
    }

    // Create the base condition object
    const condition: ConditionConfig = {
      condition: ['', Validators.required],
      operator: ['', Validators.required],
      value: [''],
      valueMode: ['users']
    };

    // Add logicOperator if not the first condition
    if (!isFirst) {
      condition.logicOperator = ['AND'];
    }

    return this.fb.group(condition);
  }

  createEquationPart(): FormGroup {
    return this.fb.group({
      operand: ['', Validators.required],
      operator: ['']
    });
  }

  addCondition() {
    this.conditions.push(this.createCondition(false));
  }

  removeCondition(index: number) {
    this.conditions.removeAt(index);
  }

  addEquationPart() {
    this.equation.push(this.createEquationPart());
  }

  removeEquationPart(index: number) {
    this.equation.removeAt(index);
  }

  onSubmit() {
    console.log('Form value:', this.logicForm.value);
    console.log('Form valid:', this.logicForm.valid);
    console.log('Form errors:', this.logicForm.errors);

    if (this.logicForm.valid) {
      console.log('form is valid')
      const formValue = this.logicForm.value;

      // Normalize group selections to user IDs for user_id + in
      const normalizedConditions = (formValue.conditions || []).map((c: any) => {
        if (c?.operator === 'in' && c?.condition === 'user_id') {
          const mode = c?.valueMode || 'users';
          const values: string[] = Array.isArray(c?.value) ? c.value : [];
          if (mode === 'groups') {
            const groupIds = new Set(values);
            const userIds: string[] = [];
            (this.groups || []).forEach((g: any) => {
              if (!groupIds.has(g.id)) return;
              if (Array.isArray(g.users)) {
                g.users.forEach((u: any) => {
                  if (u?.id && !userIds.includes(u.id)) userIds.push(u.id);
                });
              }
            });
            return { ...c, value: userIds, valueMode: 'users' };
          }
        }
        return c;
      });
      const rule: Rule = {
        conditions: normalizedConditions,
        then: {
          type: formValue.thenType,
          value: Number(formValue.thenValue),
          ...(formValue.thenType === 'logic' && {
            column: formValue.thenColumn,
            operator: formValue.thenOperator
          })
        },
        description: this.generateRuleDescription(),
        caseSensitive: formValue.caseSensitive
      };
      
      console.log('Submitting rule:', rule);
      this.logicBuilt.emit(rule);
      this.logicForm.reset({ thenType: 'logic' });
      while (this.conditions.length > 1) {
        this.conditions.removeAt(1);
      }
    } else {
      // Add this to help debug validation issues
      Object.keys(this.logicForm.controls).forEach(key => {
        const control = this.logicForm.get(key);
        console.log(`${key} validity:`, control?.valid);
        console.log(`${key} errors:`, control?.errors);
      });
    }
  }

  generateRuleDescription(): string {
    const form = this.logicForm.value;
    let conditionString = form.conditions.map((cond: any, index: number) => {
      const condition = this.projectColumns.find(col => col === cond.condition);
      const operator = this.operators.find(op => op.value === cond.operator)?.label;
      let str = `${condition} ${operator}`;
      if (!['isPresent', 'isNotPresent'].includes(cond.operator)) {
        if (cond.operator === 'in' && cond.condition === 'user_id') {
          const ids: string[] = Array.isArray(cond.value) ? cond.value : [];
          const names = ids
            .map((id: string) => {
              const u = this.users.find(x => x.id === id);
              return u ? `${u.first_name} ${u.last_name}` : id;
            })
            .join(', ');
          str += ` [${names}]`;
        } else {
          str += ` ${cond.value}`;
        }
      }
      
      // Get the next condition's logic operator if it exists
      const nextCondition = form.conditions[index + 1];
      if (nextCondition && nextCondition.logicOperator) {
        str += ` ${nextCondition.logicOperator} `;
      }
      return str;
    }).join('');

    let thenString = '';
    if (form.thenType === 'fixed') {
      thenString = `set fixed amount to ${form.thenValue}`;
    } else {
      const operator = this.arithmeticOperators.find(op => op.value === form.thenOperator)?.label?.toLowerCase();
      thenString = `${form.thenColumn} ${operator} ${form.thenValue}`;
    }

    return `If ${conditionString}, then ${thenString}`;
  }

  shouldShowComparisonValue(condition: AbstractControl, index: number): boolean {
    const operator = condition.get('operator')?.value;
    const value = condition.get('value')?.value;
    return operator !== 'isPresent' && operator !== null && operator !== '';
  }

  updateValueValidation(conditionGroup: AbstractControl, index: number) {
    const valueControl = conditionGroup.get('value');
    if (this.shouldShowComparisonValue(conditionGroup, index)) {
      valueControl?.setValidators([Validators.required]);
    } else {
      valueControl?.clearValidators();
    }
    valueControl?.updateValueAndValidity();
  }

  initForm() {
    this.logicForm = this.fb.group({
      conditions: this.fb.array([]),
      thenType: ['fixed', Validators.required],
      thenColumn: [''],
      thenOperator: [''],
      thenValue: ['', Validators.required],
      caseSensitive: [false]
    });

    this.logicForm.get('thenType')?.valueChanges.subscribe((type) => {
      this.updateLogicBasedValidators(type);
    });

    this.updateLogicBasedValidators(this.logicForm.get('thenType')?.value);
  }

  updateLogicBasedValidators(type: string) {
    const thenColumn = this.logicForm.get('thenColumn');
    const thenOperator = this.logicForm.get('thenOperator');

    if (type === 'logic') {
      thenColumn?.setValidators([Validators.required]);
      thenOperator?.setValidators([Validators.required]);
    } else {
      thenColumn?.clearValidators();
      thenOperator?.clearValidators();
    }

    thenColumn?.updateValueAndValidity({ emitEvent: false });
    thenOperator?.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit() {
    this.conditions.valueChanges.subscribe(value => {
      console.log('Conditions changed:', value);
      // Coerce value to array for user_id + in operator to support multi-select
      value.forEach((c: any, idx: number) => {
        const group = this.conditions.at(idx) as FormGroup;
        if (c?.operator === 'in' && c?.condition === 'user_id') {
          const valCtrl = group.get('value');
          if (valCtrl && !Array.isArray(valCtrl.value)) {
            valCtrl.setValue([], { emitEvent: false });
          }
        }
      });
    });
  }
}
